import React, { ChangeEvent, useCallback, useRef, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { IconButton } from "@mui/material";
import classNames from "classnames";
import { RoleComponentGuard, TooltipSpan } from "components";
import { Notice } from "components/Prompts/Notice";
import { useDidUpdate, useFormatDateHook, useIsViewerPermission, useModals, usePrompt } from "hooks";
import { usePickUserPermissions, useTaskManageActions } from "store";
import { TaskFile } from "store/types";

import { RoleName } from "../../../constants";

import "./Attachments.styles.scss";

interface Props {
  freshItems: File[];
  oldFiles?: TaskFile[];
  removeFreshFile: (lastModified: number) => void;
  removeOldFile?: (id: number) => void;
  onAddFile?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const Attachments: React.FC<Props> = ({
  onAddFile,
  freshItems,
  oldFiles = [],
  removeFreshFile,
  removeOldFile
}) => {
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();

  const formatDate = useFormatDateHook();
  const { downloadFileAction } = useTaskManageActions();
  const itemsNumber = oldFiles.length + freshItems.length;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { deleteModal } = useModals();
  const inputRef = useRef<HTMLInputElement>(null);
  const taskManager = usePickUserPermissions("task_manager");
  const isViewerPermission = taskManager && taskManager === "viewer";

  const onOpenClick = () => {
    if (!itemsNumber) return;
    setIsOpen(!isOpen);
  };

  useDidUpdate(() => {
    if (!itemsNumber && isOpen) {
      setIsOpen(false);
    }
  }, [itemsNumber]);

  const onDeleteFreshClick = useCallback(
    (file: File) => {
      deleteModal({
        element: "Delete",
        message: `Are you sure you want to delete file: ${file.name}`,
        action: async () => removeFreshFile(file.lastModified)
      });
    },
    [deleteModal, removeFreshFile]
  );

  const onDeleteOldClick = useCallback(
    (file: TaskFile) => {
      if (!removeOldFile) return;
      deleteModal({
        element: "Delete",
        message: `Are you sure you want to delete file: ${file.name}`,
        action: async () => removeOldFile(file.id)
      });
    },
    [deleteModal, removeOldFile]
  );

  const onAttachCLick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (isAnalystViewerPermission) {
        openPrompt({ Component: Notice, props: { type: "trial" } });
        return;
      }
      e.stopPropagation();
      if (inputRef.current) {
        inputRef.current.click();
      }
    },
    [inputRef]
  );

  return (
    <div className="task-attachments">
      <div onClick={onOpenClick} className="task-attachments__head">
        <div className="task-attachments__head-content">
          <span>Attachments ({itemsNumber})</span>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
            <IconButton size="sm" aria-label="more" id="long-button" aria-haspopup="true" onClick={onAttachCLick}>
              <AttachFileIcon sx={{ rotate: "30deg" }} />
            </IconButton>
          </RoleComponentGuard>
        </div>
        <ExpandMoreIcon className={classNames("icon", { "icon--reverse": !isOpen })} />
      </div>
      {isOpen && (
        <div className="task-attachments__body">
          {oldFiles.map((item) => (
            <div key={item.id} className="old-item">
              {/* <span className="name">{item.name}</span> */}
              <TooltipSpan className="name">{item.name}</TooltipSpan>
              <TooltipSpan className="publish">{`Published by: ${item.published_by}`}</TooltipSpan>
              <div className="last-part">
                <span className="date">On: {formatDate({ date: item.created_at, format: "MMM DD, YYYY" })}</span>
                <div className="buttons">
                  <IconButton
                    size="sm"
                    onClick={() => downloadFileAction({ fileId: item.id, taskId: item.task_id, fileName: item.name })}
                  >
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                  <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                    <IconButton onClick={() => onDeleteOldClick(item)} size="sm">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </RoleComponentGuard>
                </div>
              </div>
            </div>
          ))}
          {freshItems.map((item) => (
            <div key={item.lastModified} className="fresh-item">
              <TooltipSpan className="name">{item.name}</TooltipSpan>
              <div className="last-part">
                <span className="date">On: {formatDate({ date: item.lastModified, format: "MMM DD, YYYY" })}</span>
                <IconButton onClick={() => onDeleteFreshClick(item)} size="sm">
                  <DeleteOutlineIcon />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
      <input onChange={onAddFile} ref={inputRef} hidden type="file" />
    </div>
  );
};
